/*
 * Copyright 2025 iodyne.  All rights reserved.
 * Use is subject to license terms.
 */

import { 
  type CloudOrgType, 
  type SupportTicketType,
  type UpdateSupportTicketType
} from "@/lib/models/org"
import { type ModelError } from "@/lib/models/errors";
import { createEntityAdapter, type EntityState } from '@reduxjs/toolkit'
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { translate as t } from "@/i18n/client_nohook";
import { AuthStates, getAuthState } from "@/lib/auth/auth_state";
import { responseForAPIError } from "@/lib/datastore/slices/helpers";
import type { IodyneDeviceDetail } from "@/lib/models/devices";
import { devicesSlice } from "./devices";

// Endpoint Response Types

//Slices
enum RetrieveAccountErrors {
  noAccountPrivilegesError = "NoAccountPrivilegesError",
  orgDNEError = "OrgDNEError"
}

enum UpdateAccountErrors {
  noAccountPrivilegesError = "NoAccountPrivilegesError",
  orgDNEError = "OrgDNEError"
}

enum CreateTicketErrors {
  customerDeviceNotRegisteredError = "CustomerDeviceNotRegisteredError",
  accountDNEError = "OrgDNEError",
  supportTicketSubmissionError = "SupportTicketSubmissionError"
}

const orgAdapter = createEntityAdapter({
  selectId: (e:CloudOrgType) => e.org_id
});
const orgInitialState = orgAdapter.getInitialState();

const devicesAdapter = createEntityAdapter({
  selectId: (e:IodyneDeviceDetail) => e.fguid
});
const devicesInitialState = devicesAdapter.getInitialState();

const orgHelpTicketsAdapter = createEntityAdapter({
  selectId: (e:SupportTicketType) => e.ticket_no
})
const orgHelpTicketsInitialState = orgHelpTicketsAdapter.getInitialState();


export const orgSlice = createApi({
  reducerPath: "org_api",
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.IOD_CLOUD_PORTAL_ACCOUNT_API_URI}/v2/ica/org/`,
    prepareHeaders: (headers, { getState }) => {
      headers.set("Authorization", `Bearer ${getAuthState(AuthStates.userAccessToken)}`)
      return headers;
    },
  }),
  tagTypes: ["Device", "Org", "Ticket"],
  endpoints: build => ({
   
    // Retrieve Org Detail
    getOrgDetail: build.query<EntityState<CloudOrgType, string>, { org_id: string }>({
      providesTags: (result, _, arg) => [{type:"Org", id:arg.org_id}],
      query: ({ org_id }) => org_id,
      transformResponse(resp: CloudOrgType, _meta, { org_id }) {
        resp.org_id = org_id;
        return orgAdapter.setOne(orgInitialState, resp);
      },
      async transformErrorResponse(resp: { status: number, data: Record<string, any> }): Promise<ModelError> {
        return responseForAPIError(
          resp,
          {
            [RetrieveAccountErrors.noAccountPrivilegesError]: await t(
              "org_billing.org_details.api_errors.no_org_privileges"
            ),
            [RetrieveAccountErrors.orgDNEError]: await t(
              "org_billing.org_details.api_errors.org_dne"
            )
          }
        );
      }
    }),

    // Update Org Detail
    updateOrgDetail: build.mutation<EntityState<CloudOrgType, string>, { org_id: string, details: CloudOrgType }>({
      invalidatesTags: (result, error, org_id) => [
        { type: "Org", org_id: org_id }
      ],
      query: ({ org_id, details }) => ({
        url: org_id,
        method: "PUT",
        body: details
      }),
      transformResponse(resp: CloudOrgType, _meta, { org_id }) {
        resp.org_id = org_id;
        return orgAdapter.setOne(orgInitialState, resp);
      },
      async transformErrorResponse(resp: { status: number, data: Record<string, any> }): Promise<ModelError> {
        return responseForAPIError(
          resp,
          {
            [UpdateAccountErrors.noAccountPrivilegesError]: await t(
              "org_mgmt.api_errors.no_account_privileges"
            ),
            [UpdateAccountErrors.orgDNEError]: await t(
              "org_mgmt.api_errors.account_dne"
            )
          }
        );
      }
    }),

    // Update Organization Profile Art
    setProfileArt: build.mutation<EntityState<CloudOrgType, string>, { org_id: string, image: File | undefined }>({
      invalidatesTags: (result, error, org_id) => [
        { type: "Org", org_id: org_id }
      ],
      query: ({ org_id, image }) => {
        let body_form_data = new FormData();
        image && body_form_data.append("image", image);
        return {
          url: `${org_id}/art`,
          method: "POST",
          body: body_form_data,
          formData: true
        }
      }
    }),

    //Create Help Ticket from Account
    createHelpTicket: build.mutation<EntityState<SupportTicketType, number>, { org_id: string, ticket: UpdateSupportTicketType }>({
      invalidatesTags: (result, error) => [
        { type: "Ticket", ticket_no: "" }
      ],
      query: ({ org_id, ticket }) => ({
        url: `${org_id}/support/ticket`,
        method: "POST",
        body: ticket
      }),
      transformResponse(resp: SupportTicketType, _meta, { org_id }) {
        return orgHelpTicketsAdapter.setOne(orgHelpTicketsInitialState, resp);
      },
      async transformErrorResponse(resp: { status: number, data?: Record<string, any> }): Promise<ModelError> {
        return responseForAPIError(
          resp,
          {
            [CreateTicketErrors.customerDeviceNotRegisteredError]: await t(
              "ticket_mgmt.api_errors.customer_device_not_registered"
            ),
            [CreateTicketErrors.accountDNEError]: await t(
              "ticket_mgmt.api_errors.account_dne_error"
            ),
            [CreateTicketErrors.supportTicketSubmissionError]: await t(
              "ticket_mgmt.api_errors.support_ticket_submission_error",
              { err_message: resp.data ? resp.data["error_description"] : "" }
            )
          }
        )
      },
    })
  })
});

export const {
  useGetOrgDetailQuery,
  useUpdateOrgDetailMutation,
  useCreateHelpTicketMutation,
  useSetProfileArtMutation
} = orgSlice;

export const devicesSelectors = devicesAdapter.getSelectors();

